import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, Wallet, UserCheck, Contact, CalendarDays, CloudSun, Baby, ArrowRight, MapPin } from "lucide-react";
import api from "@/api";
import { useAuth } from "@/auth";
import { useLang } from "@/i18n";

function StatCard({ icon: Icon, label, value, testid, delay }) {
  return (
    <div className={`gfa-card gfa-card-hover animate-fade-up ${delay} p-6`} data-testid={testid}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-slate-500">{label}</p>
          <p className="mt-1 font-['Outfit'] text-3xl font-bold text-[#165A2E]">{value}</p>
        </div>
        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#84CC16]/15 text-[#165A2E]">
          <Icon size={22} />
        </div>
      </div>
    </div>
  );
}

const STATUS_COLOR = { scheduled: "bg-blue-100 text-blue-700", ongoing: "bg-amber-100 text-amber-700", cancelled: "bg-red-100 text-red-700", completed: "bg-emerald-100 text-emerald-700" };

export default function Dashboard() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <p className="text-slate-500" data-testid="dashboard-loading">{t("loading")}</p>;
  const isParent = user.role === "parent";

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div>
        <h1 className="font-['Outfit'] text-2xl font-bold text-[#165A2E] md:text-3xl">{t("dashboard")}</h1>
        <p className="text-sm text-slate-500">{user.name} · {user.role}</p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {isParent ? (
          <>
            <StatCard icon={Baby} label={t("myChildren")} value={data.my_players} testid="stat-my-players" delay="stagger-1" />
            <StatCard icon={Wallet} label={t("outstanding")} value={`RM ${data.outstanding.toFixed(2)}`} testid="stat-outstanding" delay="stagger-2" />
            <StatCard icon={CalendarDays} label={t("upcoming")} value={data.upcoming.length} testid="stat-upcoming" delay="stagger-3" />
            <StatCard icon={CloudSun} label={t("inquiries")} value={data.inquiries.length} testid="stat-inquiries" delay="stagger-4" />
          </>
        ) : (
          <>
            <StatCard icon={Users} label={t("totalPlayers")} value={data.players} testid="stat-players" delay="stagger-1" />
            <StatCard icon={Wallet} label={t("pendingFees")} value={`RM ${data.outstanding.toFixed(2)}`} testid="stat-outstanding" delay="stagger-2" />
            <StatCard icon={UserCheck} label={t("coaches")} value={data.coaches} testid="stat-coaches" delay="stagger-3" />
            <StatCard icon={Contact} label={t("parents")} value={data.parents} testid="stat-parents" delay="stagger-4" />
          </>
        )}
      </div>

      {!isParent && (
        <div className="gfa-card animate-fade-up stagger-3 p-6" data-testid="coach-today-card">
          <h2 className="mb-4 font-['Outfit'] text-lg font-bold text-[#165A2E]">{t("coachToday")}</h2>
          {(data.today_punches || []).length === 0 ? (
            <p className="text-sm text-slate-400">{t("noData")}</p>
          ) : (
            <div className="space-y-2">
              {data.today_punches.map((p) => (
                <div key={p.punch_id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-100 p-3 text-sm" data-testid={`coach-punch-${p.punch_id}`}>
                  <span className="font-semibold text-slate-800">{p.name}</span>
                  <span className="text-xs text-slate-500">{t("inTime")} {p.in_time}{p.out_time ? ` · ${t("outTime")} ${p.out_time}` : ""}</span>
                  <span className="flex gap-2">
                    {p.in_loc && (
                      <a href={`https://www.google.com/maps?q=${p.in_loc.lat},${p.in_loc.lon}`} target="_blank" rel="noreferrer" data-testid={`punch-loc-in-${p.punch_id}`}
                        className="inline-flex items-center gap-1 rounded-full bg-[#165A2E]/10 px-3 py-1 text-xs font-semibold text-[#165A2E] hover:bg-[#165A2E] hover:text-white">
                        <MapPin size={11} /> {t("inTime")} GPS
                      </a>
                    )}
                    {p.out_loc && (
                      <a href={`https://www.google.com/maps?q=${p.out_loc.lat},${p.out_loc.lon}`} target="_blank" rel="noreferrer" data-testid={`punch-loc-out-${p.punch_id}`}
                        className="inline-flex items-center gap-1 rounded-full bg-[#84CC16]/20 px-3 py-1 text-xs font-semibold text-[#165A2E] hover:bg-[#84CC16]">
                        <MapPin size={11} /> {t("outTime")} GPS
                      </a>
                    )}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="gfa-card animate-fade-up stagger-2 p-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-['Outfit'] text-lg font-bold text-[#165A2E]">{t("upcoming")}</h2>
          <Link to="/app/schedule" data-testid="dashboard-view-schedule" className="flex items-center gap-1 text-sm font-semibold text-[#165A2E] hover:underline">
            {t("view")} <ArrowRight size={14} />
          </Link>
        </div>
        {data.upcoming.length === 0 ? (
          <p className="text-sm text-slate-400">{t("noData")}</p>
        ) : (
          <div className="space-y-3">
            {data.upcoming.map((s) => (
              <div key={s.schedule_id} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-100 p-4" data-testid={`upcoming-${s.schedule_id}`}>
                <div>
                  <p className="font-semibold text-slate-800">{s.title}</p>
                  <p className="text-xs text-slate-500">{s.date} · {s.start_time} - {s.end_time} · {s.location_name}</p>
                </div>
                <span className={`rounded-full px-3 py-1 text-xs font-semibold ${STATUS_COLOR[s.status] || ""}`} data-testid={`upcoming-status-${s.schedule_id}`}>
                  {t(s.status)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
